import React, { useState, createContext, useEffect, useCallback } from 'react'
import { setPreference, usePreference } from '@fs/zion-preferences'
import { trackEvent } from '@fs/zion-analytics'

const DarkModeContext = createContext({})

const DARK_MODE_PREFERENCE_KEY = 'search.darkMode'

/**
 * Provides dark mode state and allows toggling to/from its children via context.
 * To enable dark mode on a page, the page must call the `useAllowDarkMode` function from context.
 * This is to prevent dark mode from being enabled on pages that should not have it.
 * @see useAllowDarkMode function below.
 * @param {object} props - The props object.
 * @param {React.ReactNode} props.children - The child components that will have access to dark mode context.
 * @returns {JSX.Element} The DarkModeProvider component.
 */
export default function DarkModeProvider({ children }) {
  const [isDarkModeOn, setIsDarkModeOn] = useState()

  const setIsDarkMode = useCallback(async (isOn) => {
    setIsDarkModeOn(isOn)
    await setPreference(DARK_MODE_PREFERENCE_KEY, isOn)
  }, [])

  const initialDarkModePreference = usePreference(DARK_MODE_PREFERENCE_KEY)

  // usePreference never updates the value of darkModeOn @see https://beta.familysearch.org/frontier/zion/?path=/docs/utilities-user-preferences--docs#usepreference
  useEffect(() => {
    if (initialDarkModePreference) setIsDarkModeOn(initialDarkModePreference)
    else setIsDarkModeOn(false)
  }, [initialDarkModePreference])

  const [isDarkModeAllowedOnPage, setIsDarkModeAllowedOnPage] = useState(false)

  const theme = isDarkModeOn && isDarkModeAllowedOnPage ? 'night' : 'day'

  // this must be called from the page level in order to allow dark mode on that page
  const useAllowDarkMode = () => {
    useEffect(() => {
      if (typeof setIsDarkModeAllowedOnPage !== 'function') {
        throw new Error('useAllowDarkMode must be used within a DarkModeProvider')
      }
      setIsDarkModeAllowedOnPage(true)
      return () => {
        // must set back on unmount so other pages that we don't want dark mode on don't get it (due to React Router)
        setIsDarkModeAllowedOnPage(false)
      }
    }, [])
  }

  const darkModeContextValue = React.useMemo(
    () => ({
      isDarkModeOn,
      loading: typeof isDarkModeOn === 'undefined',
      setIsDarkModeOn: (isOn) => {
        setIsDarkMode(isOn)
        trackEvent({
          event_name: 'click_action',
          link_name: 'Click on Dark Mode Toggle',
        })
      },
      theme,
      useAllowDarkMode,
    }),
    [isDarkModeOn, setIsDarkMode, theme]
  )
  return <DarkModeContext.Provider value={darkModeContextValue}>{children}</DarkModeContext.Provider>
}
export { DarkModeContext }
